import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  FormLayout,
  TextField,
  Select,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Divider,
  Thumbnail
} from '@shopify/polaris';
import { UploadIcon } from '@shopify/polaris-icons';
import axios from 'axios';
import { withApiErrorHandling } from '../utils/apiErrorHandler';

interface PromptCategory {
  id: number;
  name: string;
  description?: string;
}

interface AiSettings {
  auto_enabled_on_call: boolean;
  tenant_context?: string;
}

interface CallCreationFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CallCreationForm: React.FC<CallCreationFormProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    phone: '',
    direction: 'inbound',
    status: 'answered',
    vendor: '',
    call_sid: '',
    external_id: '',
    category_id: '',
    recording_url: '',
    called_at: new Date().toISOString().slice(0, 16) // YYYY-MM-DDTHH:MM format
  });

  const [recordingFile, setRecordingFile] = useState<File | null>(null);
  const [recordingType, setRecordingType] = useState<'url' | 'file'>('url');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<PromptCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [aiSettings, setAiSettings] = useState<AiSettings | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const result = await withApiErrorHandling(
        () => axios.get('/ai_settings/prompt_categories')
      ) as any;
      
      if (result && result.success) {
        setCategories(result.data.prompt_categories || []);
        // Set default category if available
        if (result.data.prompt_categories?.length > 0 && !formData.category_id) {
          setFormData(prev => ({
            ...prev,
            category_id: result.data.prompt_categories[0].id.toString()
          }));
        }
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  }, [formData.category_id]);

  const loadAiSettings = useCallback(async () => {
    try {
      const result = await withApiErrorHandling(
        () => axios.get('/ai_settings')
      ) as any;
      
      if (result && result.success) {
        setAiSettings(result.data.ai_settings);
      }
    } catch (error) {
      console.error('Error loading AI settings:', error);
    }
  }, []);

  // Load prompt categories and AI settings on component mount
  useEffect(() => {
    if (open) {
      loadCategories();
      loadAiSettings();
    }
  }, [open, loadCategories, loadAiSettings]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/x-m4a'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setError('Please select a valid audio file (MP3, WAV, M4A).');
        return;
      }
      
      setRecordingFile(selectedFile);
      setError(null);
    }
  };

  const validateForm = () => {
    const errors: string[] = [];

    // Either call_sid or external_id is required
    if (!formData.call_sid.trim() && !formData.external_id.trim()) {
      errors.push('Either Call SID or External ID is required');
    }

    // Category is required
    if (!formData.category_id) {
      errors.push('Category is required');
    }

    // Recording is required
    if (recordingType === 'url') {
      if (!formData.recording_url.trim()) {
        errors.push('Recording URL is required');
      } else {
        try {
          new URL(formData.recording_url);
        } catch {
          errors.push('Please enter a valid recording URL');
        }
      }
    } else if (recordingType === 'file') {
      if (!recordingFile) {
        errors.push('Recording file is required');
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    if (loading) return; // Prevent multiple submissions
    
    setError(null);
    const validationErrors = validateForm();
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Add form fields
      submitData.append('call_log[phone]', formData.phone);
      submitData.append('call_log[direction]', formData.direction);
      submitData.append('call_log[status]', formData.status);
      submitData.append('call_log[vendor]', formData.vendor);
      submitData.append('call_log[call_sid]', formData.call_sid);
      submitData.append('call_log[external_id]', formData.external_id);
      submitData.append('call_log[prompt_category_id]', formData.category_id);
      submitData.append('call_log[called_at]', formData.called_at);

      // Handle recording
      if (recordingType === 'file' && recordingFile) {
        submitData.append('call_log[recording]', recordingFile);
      } else if (recordingType === 'url' && formData.recording_url.trim()) {
        // For URL, we'll need to handle this on the backend
        submitData.append('call_log[recording_url]', formData.recording_url);
      }

      try {
        const response = await axios.post('/call_logs', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const result = response.data;
        if (result && result.success) {
          showToast('Call log created successfully!');
          onSuccess();
          handleClose();
        } else {
          // Display the specific error message from the backend
          setError(result?.error || 'Failed to create call log');
        }
      } catch (error: any) {
        console.error('Error creating call log:', error);
        
        // Handle different types of errors
        if (error?.response?.data?.error) {
          // Backend validation error - this is what we want to show
          setError(error.response.data.error);
        } else if (error?.response?.data?.message) {
          // Alternative error format
          setError(error.response.data.message);
        } else if (error?.message) {
          // Network or other error
          setError(error.message);
        } else {
          setError('Failed to create call log. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return; // Prevent closing while submitting
    
    setFormData({
      phone: '',
      direction: 'inbound',
      status: 'answered',
      vendor: '',
      call_sid: '',
      external_id: '',
      category_id: '',
      recording_url: '',
      called_at: new Date().toISOString().slice(0, 16)
    });
    setRecordingFile(null);
    setRecordingType('url');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const showToast = (message: string) => {
    // This would typically use a toast context, but for now we'll just log
    console.log(message);
  };

  const categoryOptions = categories.map(category => ({
    label: category.name,
    value: category.id.toString()
  }));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create New Call Log"
      primaryAction={{
        content: 'Create Call Log',
        onAction: handleSubmit,
        loading: loading,
        disabled: loading
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleClose
        }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          <FormLayout>
            {/* Required Fields Section */}
            <Text variant="headingMd" as="h3">
              Required Information
            </Text>
            
            <FormLayout.Group>
              <TextField
                label="Call SID"
                value={formData.call_sid}
                onChange={(value) => setFormData(prev => ({ ...prev, call_sid: value }))}
                placeholder="Enter call SID"
                helpText="Unique identifier from your telephony system"
                autoComplete="off"
              />
              <TextField
                label="External ID"
                value={formData.external_id}
                onChange={(value) => setFormData(prev => ({ ...prev, external_id: value }))}
                placeholder="Enter external ID"
                helpText="Alternative unique identifier"
                autoComplete="off"
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Select
                label="Category"
                options={loadingCategories ? [{ label: 'Loading...', value: '' }] : categoryOptions}
                value={formData.category_id}
                onChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                disabled={loadingCategories}
                requiredIndicator
                helpText="Select the prompt category for AI analysis"
              />
            </FormLayout.Group>

            <Divider />

            <Text variant="headingMd" as="h3">
              Recording (Required)
            </Text>
            <Text variant="bodyMd" as="p" tone="subdued">
              Add a call recording for transcription and AI analysis. You can either provide a URL or upload a file.
            </Text>

            <FormLayout.Group>
              <Select
                label="Recording Source"
                options={[
                  { label: 'URL', value: 'url' },
                  { label: 'File Upload', value: 'file' }
                ]}
                value={recordingType}
                onChange={(value) => setRecordingType(value as 'url' | 'file')}
              />
            </FormLayout.Group>

            {recordingType === 'url' ? (
              <TextField
                label="Recording URL"
                value={formData.recording_url}
                onChange={(value) => setFormData(prev => ({ ...prev, recording_url: value }))}
                placeholder="https://example.com/recording.mp3"
                helpText="URL to download the call recording (MP3, WAV, M4A, etc.)"
                autoComplete="url"
                requiredIndicator
              />
            ) : (
              <BlockStack gap="200">
                <Text variant="bodyMd" as="p" fontWeight="medium">
                  Recording File
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  Upload an audio file (MP3, WAV, M4A, etc.)
                </Text>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                
                <Button
                  icon={UploadIcon}
                  onClick={() => fileInputRef.current?.click()}
                  fullWidth
                >
                  Choose Audio File
                </Button>
              </BlockStack>
            )}

            {recordingFile && (
              <InlineStack gap="200" align="start">
                <Thumbnail
                  source={UploadIcon}
                  alt="Audio file"
                  size="small"
                />
                <BlockStack gap="100">
                  <Text variant="bodyMd" as="span" fontWeight="medium">
                    {recordingFile.name}
                  </Text>
                  <Text variant="bodySm" as="span" tone="subdued">
                    {(recordingFile.size / 1024 / 1024).toFixed(2)} MB
                  </Text>
                </BlockStack>
                <Button
                  size="slim"
                  onClick={() => {
                    setRecordingFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  Remove
                </Button>
              </InlineStack>
            )}

            <Divider />

            {/* Optional Fields Section */}
            <Text variant="headingMd" as="h3">
              Optional Information
            </Text>
            
            <FormLayout.Group>
              <TextField
                label="Phone Number"
                value={formData.phone}
                onChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
                placeholder="Enter phone number (optional)"
                helpText="Phone number of the caller or called party"
                autoComplete="tel"
              />
              <Select
                label="Direction"
                options={[
                  { label: 'Inbound', value: 'inbound' },
                  { label: 'Outbound', value: 'outbound' }
                ]}
                value={formData.direction}
                onChange={(value) => setFormData(prev => ({ ...prev, direction: value }))}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Select
                label="Status"
                options={[
                  { label: 'Answered', value: 'answered' },
                  { label: 'Not Answered', value: 'not_answered' }
                ]}
                value={formData.status}
                onChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
              />
              <TextField
                label="Vendor"
                value={formData.vendor}
                onChange={(value) => setFormData(prev => ({ ...prev, vendor: value }))}
                placeholder="Enter vendor name (optional)"
                helpText="Service provider or system that handled the call"
                autoComplete="off"
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Call Date & Time"
                type="datetime-local"
                value={formData.called_at}
                onChange={(value) => setFormData(prev => ({ ...prev, called_at: value }))}
                helpText="When the call was made"
                autoComplete="off"
              />
            </FormLayout.Group>

            <Banner tone="info">
              <p>
                <strong>Note:</strong> Either Call SID or External ID is required for unique identification. 
                Recording is required for AI transcription and analysis.
                {aiSettings?.auto_enabled_on_call && (
                  <><br /><br />
                  <strong>Auto AI Analysis:</strong> Your recording will be automatically transcribed and analyzed using AI when the call is created.
                  </>)
                }
              </p>
            </Banner>
          </FormLayout>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default CallCreationForm;
